import { useAutoFocus } from '@/scripts/hooks';
import { useChatContext } from '@/contexts/chat/context';
import { useAudio } from '@/scripts/hooks/useAudio';
import { isDebug } from '@/scripts/utils/devUtils';
import type { Chat, ChatParams } from '@/contexts/chat/scripts/hooks/useChats/types';
import {
  ChatMessagePayloadType,
  type SendMessageParams,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { isValidMessage } from '@/scripts/utils/isValidMessage';
import ChatInputExtend from '../chat-input-extend';
import {
  insertNode,
  handleInput,
  handleKeyDown,
  handlePaste,
  focusAndMoveToEnd,
} from './helpers';
import classes from './index.module.scss';

export interface ChatInputProps {
  className?: ClassNames;
  chat: Chat;
  activatedChat?: Chat;
  onSend: (value: SendMessageParams, chat: Chat) => void;
  updateChats: (chats: ChatParams[]) => Promise<void>;
}

export default function ChatInput({
  className,
  chat,
  activatedChat,
  onSend,
  updateChats,
}: ChatInputProps) {
  const {
    encryptMsg,
    fileMessages: { pickFilesUtil, getPickInfoEventHandlers },
  } = useChatContext();

  const [payload, setPayload] = useState('');
  const inputTextAreaRef = useRef<HTMLDivElement>(null);

  const { play: playSendSound } = useAudio({ src: '/media/send.mp3' });

  useAutoFocus(inputTextAreaRef.current!);

  const onSend_ = useCallback(
    (payload: string, params?: Partial<SendMessageParams>) => {
      const repeatArgs = isDebug && /^\/repeat(?:\s(\d+))?(?:\s(.+))?/.exec(payload);
      if (repeatArgs) {
        const date = new Date().toLocaleString();

        let [, num, text] = repeatArgs;
        num = num ? Number(num) : 20;

        text = text || `Test text from test send 👽👽👽.`;

        onSend_(
          `------------------------------------ Start [${date}] ------------------------------------`
        );
        let j = 0;
        while (j < num) {
          onSend_(`[${j}] --- ${text}`);
          j++;
        }
        onSend_(
          `------------------------------------ End ------------------------------------`
        );
      } else {
        onSend(
          {
            payload_type: encryptMsg
              ? ChatMessagePayloadType.ENCRYPTED_TEXT
              : ChatMessagePayloadType.TEXT,
            ...params,
            payload,
          },
          chat
        );
      }
    },
    [chat, encryptMsg, onSend]
  );

  const send = useCallback(() => {
    if (isValidMessage(payload)) {
      playSendSound();
      onSend_(payload);
      inputTextAreaRef.current!.innerHTML = '';
      inputTextAreaRef.current!.focus();
      setPayload('');
    }
  }, [payload, playSendSound, onSend_]);

  const insertChar = useCallback((value: string) => {
    insertNode({
      node: document.createTextNode(value),
      inputRef: inputTextAreaRef,
      onPayloadChange: setPayload,
    });
  }, []);

  const insertLineBreak = useCallback(() => {
    insertNode({
      node: document.createElement('br'),
      inputRef: inputTextAreaRef,
      onPayloadChange: setPayload,
    });
  }, []);

  const eventHandlers = useMemo(
    () => getPickInfoEventHandlers(chat),
    [chat, getPickInfoEventHandlers]
  );

  const eventHandlers_image = useMemo(
    () => getPickInfoEventHandlers(chat, { accept: 'img' }),
    [chat, getPickInfoEventHandlers]
  );

  useLayoutEffect(() => {
    if (!payload || inputTextAreaRef.current!.innerHTML === '<br>') {
      inputTextAreaRef.current?.focus();
      inputTextAreaRef.current!.innerHTML = '';
    }
  }, [payload]);

  useEffect(() => {
    if (chat.chat_id === activatedChat?.chat_id) {
      focusAndMoveToEnd(inputTextAreaRef.current);
    }
  }, [activatedChat?.chat_id, chat.chat_id]);

  return (
    <>
      <div
        className={cls(classes.chatInput, className, {
          [classes.dragging]: pickFilesUtil.isDragging,
        })}
        onClick={() => inputTextAreaRef.current?.focus()}
        {...{ ...eventHandlers.drop, ...eventHandlers.paste }}
      >
        <div className={classes.inputTop}>
          <div
            contentEditable
            ref={inputTextAreaRef}
            className={cls(classes.input, 'scrollbar_hover')}
            aria-placeholder={`Message ${chat.name}`}
            onInput={(e: React.FormEvent<HTMLDivElement>) =>
              handleInput(e as React.ChangeEvent<HTMLDivElement>, setPayload)
            }
            onKeyDown={(e) => handleKeyDown(e, insertLineBreak, send)}
            onPaste={(e) => handlePaste(e)}
          ></div>
          <ChatInputExtend
            chat={chat}
            updateChats={updateChats}
            value={payload}
            onSend={send}
            onEmojiClick={insertChar}
            onPicturesSelect={eventHandlers_image.click.onClick}
            onFilesSelect={eventHandlers.click.onClick}
          />
        </div>
      </div>
    </>
  );
}
